import React, { Component } from "react";
import { Provider } from "react-redux";
import { ConnectedRouter } from 'connected-react-router'
import { persistStore } from 'redux-persist'

import store, { history } from 'src/app/store'
import ErrorBoundary from 'src/components/ErrorBoundary'
import { Loader } from 'src/components/Loader'
import networkService from 'src/app/network'
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles'
import { getPlain } from 'src/utils/request-client'
import {
  getBrandingCssUri,
  getBodyContainer,
  logError,
  getLogLevel,
  hasLocalStorage,
  getBrandSettings,
  isEmpty,
  getErrorMessage,
  isNodeLocal
} from 'src/utils/helpers'
import BrokerService from 'src/utils/broker-service'
import LanguageWrapper from 'src/components/LanguageWrapper'
import notification from 'src/components/Notification'
import { setSettings, setLanguage, clearAppCache } from './actions'

// For widget
import { WidgetContext, widgetContextSettings } from 'src/modules/App/WidgetContext'
// MemoryRouter is used for "history" context in widget iframe
import { MemoryRouter } from 'react-router'

import AppLayout from './AppLayout.container'

import 'typeface-roboto'
import Accessibe from 'src/components/Accessibe'

import './App.less'

class App extends Component {
  state = {
    rehydrate: false,
    colors: {},
  }

  componentDidMount() {
    // Use redux persist store only if localStorage (cookies) are enabled
    if (hasLocalStorage()) {

      // TODO: Check if we should use different storage engine:
      // https://github.com/rt2zz/redux-persist/tree/v4.6.0#storage-engines
      persistStore(store, {
        // List of reducers not to persist in storage (also set in src/app/offline-config.js)
        blacklist: [
          'nonPersistentAppReducer',
        ]
      }, this.initialize)
    } else {
      this.initialize()
    }

    networkService.setupInterceptors(store)

    // Set to which DOM element would global notifications be attached
    notification.config({
      getContainer: () => getBodyContainer()
    })
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { isWidget, isWidgetFullSize } = this.props

    // Re-render if widget and "full size" state changes
    if (isWidget && nextProps.isWidgetFullSize !== isWidgetFullSize) {
      return true
    }

    const { nonPersistentAppReducer } = store.getState()

    // Check if app is rendered based on global redux state
    // It's global so we can re-render whole app if needed (e.g. language change)
    const isAppRendered = nonPersistentAppReducer.hasOwnProperty('isAppRendered') && nonPersistentAppReducer.isAppRendered === true

    // If it's not rendered it should update (re-render)
    return !isAppRendered
  }

  initialize = async () => {
    try {
      const { isWidget, widgetLanguage, broker } = this.props

      if (isWidget) {
        // Set broker if passed from widget script
        if (broker) {
          BrokerService.setBroker(broker)
        }

        // Set language if passed from widget script to global redux store
        if (widgetLanguage) {
          try {
            // Setting language with
            store.dispatch(setLanguage(widgetLanguage, false, 'widget'))
          } catch (e) {
            logError('Dispatch set language', e)
          }
        }
      }

      await this.handleCoreSettings()

      const settings = await this.handleSettings()
      if (isEmpty(settings)) {
        return false
      }

      if (typeof settings === 'object' && settings.hasOwnProperty('error')) {
        notification.error({
          message: 'Error',
          description: getErrorMessage(settings.error),
          duration: 0,
          getContainer: () => getBodyContainer(),
        })

        return false
      }

      if (!isWidget) {
        this.applyAppName(settings)
      }

      this.setState({
        rehydrate: true
      })

      return true
    } catch (e) {
      logError('On app initialize', e)
      return false
    }
  }

  /**
   * Get only core settings, to set favicon and splash screen as quickly as possible.
   *
   * @return {boolean}
   */
  handleCoreSettings = async () => {
    try {
      const { isWidget } = this.props

      // Not needed for widget
      if (isWidget) {
        return false
      }

      const coreSettings = await getBrandSettings('c')
      if (!(coreSettings && coreSettings.hasOwnProperty('graphics'))) {
        return false
      }

      this.applyIcons(coreSettings.graphics)
      this.applySplashScreens(coreSettings.graphics)

      return true
    } catch (e) {
      logError('Handle core settings', e)
    }
  }

  /**
   * Get settings from backend and apply then to global redux store.
   *
   * @return {object}
   */
  handleSettings = async () => {
    try {
      const { isWidget } = this.props

      // Currently backend returns same settings for these two initial referrers
      const settingsReferer = isWidget ? 'i-w' : 'i-a'

      // Get all settings, first widget settings were just for colors, when using: getBrandSettings ('w')
      const settings = await getBrandSettings(settingsReferer)
      if (typeof settings !== 'object' || isEmpty(settings) || settings.hasOwnProperty('error')) {
        return settings
      }

      if (!isWidget && settings.hasOwnProperty('brand') && settings.brand && settings.brand.hasOwnProperty('colors')) {
        await this.applyBrandColors(settings.brand.colors).catch(e => {
          logError('Apply brand colors', e, getLogLevel(e))
        })
      }

      // Set broker settings to global redux store
      store.dispatch(setSettings(settings))

      return settings

    } catch (e) {
      logError('Handle settings', e)
    }
  }

  applyBrandColors = async (colors) => {
    this.setState({
      colors: colors,
    })

    const response = await getPlain(getBrandingCssUri(colors))

    if (!isEmpty(response) && response.hasOwnProperty('css')) {
      const style = document.createElement('style');
      style.id = "kiosk-branding-color"
      style.appendChild(document.createTextNode(response.css));
      document.body.appendChild(style);
    }

    const themeColorEl = document.querySelector('#theme-color');
    if (themeColorEl) {
      themeColorEl.setAttribute('content', colors.brandColor);
    }
    return Promise.resolve()
  }

  applyAppName = (settings) => {
    try {
      if (!settings.hasOwnProperty('brand') || !settings.brand.hasOwnProperty('name') || isEmpty(settings.brand.name)) {
        return false;
      }

      document
        .getElementById('apple-mobile-web-app-title')
        .setAttribute('content', settings.brand.name);

    } catch (e) {
      logError('Could not apply app name', e)
    }
  }

  applyIcons = (graphics) => {
    try {
      if (isEmpty(graphics) || !graphics.hasOwnProperty('icons') || isEmpty(graphics.icons)) {
        return false;
      }

      const iconsToAppendToDom = ['favicon', 'apple-touch-icon']
      graphics.icons.forEach((icon) => {
        if (iconsToAppendToDom.includes(icon.name)) {
          document
            .getElementById(icon.name)
            .setAttribute('href', icon.src);
        }
      })
    } catch (e) {
      logError('Could not apply favicon', e)
    }
  }

  applySplashScreens = (graphics) => {
    try {
      if (isEmpty(graphics) || !graphics.hasOwnProperty('screen') || isEmpty(graphics.screen)) {
        return false;
      }

      graphics.screens.forEach((screen) => {
        document
          .getElementById(screen.name)
          .setAttribute('href', screen.src);
      })
    } catch (e) {
      logError('Could not apply splash screens', e)
    }
  }

  render() {
    const {
      broker,
      isWidget,
      isWidgetActive,
      isWidgetFullSize,
      handleCloseWidget,
      initialWidgetFeature,
    } = this.props
    const { rehydrate, colors } = this.state

    if (!rehydrate) {
      return <Loader />
    }

    const getWidgetContext = {
      ...widgetContextSettings,
      isWidget: !!isWidget,
      isWidgetActive: !!isWidgetActive,
      isWidgetFullSize: !!isWidgetFullSize,
      handleCloseWidget: handleCloseWidget,
      initialWidgetFeature: initialWidgetFeature,
    }

    // TODO: PAT-2954 Ask designer to configure colors: https://material-ui.com/customization/color/,
    //  and other theme settings: https://material-ui.com/customization/default-theme/
    const muiTheme = createMuiTheme({
      palette: {
        kioskColors: colors,
      },
    })

    const OptionalWrapper = ({ condition, wrap, children }) => (
      condition ? wrap(children) : children
    )

    return (
      <Provider store={store}>
        {!isNodeLocal() && <Accessibe />}
        <LanguageWrapper>
          <ConnectedRouter history={history}>
            <OptionalWrapper
              condition={!isWidget}
              wrap={children => (<ThemeProvider theme={muiTheme}>{children}</ThemeProvider>)}
            >
              <ErrorBoundary clearAppCache={() => store.dispatch(clearAppCache())}>
                <div id="kiosk-body-container" className="kiosk-body-container" />
                <WidgetContext.Provider value={getWidgetContext}>
                  <OptionalWrapper
                    condition={isWidget}
                    wrap={children => (<MemoryRouter>{children}</MemoryRouter>)}
                  >
                    <AppLayout
                      broker={broker}
                      isWidget={!!isWidget}
                      isWidgetActive={!!isWidgetActive}
                      isWidgetFullSize={!!isWidgetFullSize}
                      handleCloseWidget={handleCloseWidget}
                    />
                  </OptionalWrapper>
                </WidgetContext.Provider>
              </ErrorBoundary>
            </OptionalWrapper>
          </ConnectedRouter>
        </LanguageWrapper>
      </Provider>
    );
  }
}

export default App;
